import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, ProviderName, FitnessTrackerService, InputType, config, InputField } from '@fgb/core';

@Component({
  selector: 'fgb-fitness-tracker-callback',
  templateUrl: './fitness-tracker-callback.component.html',
  styleUrl: './fitness-tracker-callback.component.scss',
})
export class FitnessTrackerCallbackComponent implements OnInit {
  constructor(
    private fitnessTrackerService: FitnessTrackerService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService
  ) { }

  /**
   * If the user is already authenticated, redirect them back to the
   * fitness tracker page. Otherwise, process the callback returned
   * by the external fitness tracker provider.
   */
  ngOnInit() {
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/fitness-tracker']);
    } else {
      this.authenticateCode();
    }
  }

  /**
   * Authenticates the user using the callback data returned
   * from the external fitness tracker provider.
   *
   * The provider id is retrieved from the route parameters,
   * while all query string parameters returned by the provider
   * are converted into InputField objects and passed directly
   * to the API for validation.
   *
   * If validation fails, the user is redirected back to the
   * fitness tracker page.
   */
  authenticateCode(): void {
    const providerId = Number(
      this.route.snapshot.paramMap.get('providerId')
    ) as ProviderName;

    const loginUrlParameters = this.generateLoginParameters(providerId);

    this.fitnessTrackerService
      .validateCode(providerId, loginUrlParameters)
      .toPromise();
    this.router.navigate(['/fitness-tracker']);
  }

  /**
   * Creates the login parameters required by the validation API.
   *
   * All query string parameters returned by the external provider
   * are converted into InputField objects. The callback redirect URL
   * is also included so the API can validate the OAuth response for
   * the current provider.
   *
   * @param providerId The external fitness tracker provider id.
   * @returns Array of InputField objects to send to the validation API.
   */
  generateLoginParameters(providerId: ProviderName): InputField[] {
    const queryParams = this.route.snapshot.queryParams;

    const loginParameters: InputField[] = Object.keys(queryParams).map((key) => ({
      FieldName: key,
      Value: queryParams[key],
      InputType: InputType.Text,
      Active: true,
      ErrorMessage: '',
    }));

    loginParameters.push({
      FieldName: 'redirectUrl',
      Value: `${config.portalUrl}/provider/${providerId}/connect-callback`,
      InputType: InputType.Text,
      Active: true,
      ErrorMessage: '',
    });

    return loginParameters;
  }
}
